// views/DeliveryFee/MoneySplitPanel.jsx
// ─────────────────────────────────────────────────────────────────────────────
// "Where your money goes" — renders Contract A's `split` block as a clear
// stacked bar + per-party rows (Lusuku commission / partner earnings / rider
// earnings), and surfaces the engine `flags` as friendly, explained badges.
//
// `split.mode` tells us which economic split is in force (e.g. third_party vs
// in_house / vendor_own). We label the parties from that mode but never hide the
// split itself — every party that earns is shown to both vendors and admins.
// ─────────────────────────────────────────────────────────────────────────────

import React from "react";
import {
  FiPieChart, FiTruck, FiUser, FiHome, FiFlag, FiInfo,
} from "react-icons/fi";
import {
  C, formatUGX, formatPct, badgeStyle, flagMeta, toneColors,
} from "./feeUiKit.js";

// Order + presentation for each split party. We read amounts defensively because
// the in-house split may zero out partnerEarnings.
const PARTY_DEFS = [
  { key: "lusukuCommission", label: "Lusuku commission", sub: "Platform & operations", icon: <FiHome size={13} />, color: C.blue },
  { key: "partnerEarnings",  label: "3PL partner",        sub: "Fleet partner share",   icon: <FiTruck size={13} />, color: C.purple },
  { key: "riderEarnings",    label: "Rider earnings",     sub: "Paid to the rider",     icon: <FiUser size={13} />,  color: C.teal },
];

const MODE_LABEL = {
  third_party: "3PL partner fleet",
  vendor_own:  "Vendor's own riders",
  in_house:    "Lusuku dedicated riders",
};

export default function MoneySplitPanel({ split, fee, flags }) {
  if (!split) return null;

  const total = Number(split.sum ?? fee ?? 0) || 0;
  const parties = PARTY_DEFS.map((p) => ({ ...p, amount: Number(split[p.key] || 0) }));
  // Only render a party row when it actually earns (keeps vendor_own / in_house clean).
  const visible = parties.filter((p) => p.amount > 0);
  const pct = (amt) => (total > 0 ? (amt / total) * 100 : 0);

  const modeLabel = MODE_LABEL[split.mode] || split.mode || "—";
  const flagList = Array.isArray(flags) ? flags : [];

  return (
    <div>
      {/* Mode chip */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <FiPieChart size={14} color={C.blue} />
        <span style={{ fontSize: 12, color: C.textMid }}>Split mode</span>
        <span style={{ ...badgeStyle(C.blue, C.blueSoft), marginLeft: 2 }}>{modeLabel}</span>
        <span style={{ marginLeft: "auto", fontSize: 12, color: C.textLight }}>
          of <strong style={{ color: C.text }}>{formatUGX(total)}</strong>
        </span>
      </div>

      {/* Stacked bar */}
      <div style={{
        height: 14, borderRadius: 7, overflow: "hidden", display: "flex",
        marginBottom: 4, background: C.border,
      }}>
        {visible.map((p) => (
          <div
            key={p.key}
            style={{ flex: Math.max(pct(p.amount), 0.001), background: p.color, transition: "flex 0.3s" }}
            title={`${p.label}: ${formatUGX(p.amount)} (${formatPct(pct(p.amount))})`}
          />
        ))}
      </div>
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 14 }}>
        {visible.map((p) => (
          <span key={p.key} style={{ fontSize: 10, color: p.color, fontWeight: 700 }}>
            ■ {p.label.split(" ")[0]} {formatPct(pct(p.amount))}
          </span>
        ))}
      </div>

      {/* Per-party rows */}
      {visible.map((p) => (
        <div key={p.key} style={{
          display: "flex", alignItems: "center", gap: 11,
          padding: "9px 12px", borderRadius: C.radiusSm,
          border: `1px solid ${C.border}`, marginBottom: 6,
        }}>
          <span style={{
            width: 30, height: 30, borderRadius: 8, background: `${p.color}14`,
            color: p.color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}>
            {p.icon}
          </span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{p.label}</div>
            <div style={{ fontSize: 11, color: C.textLight }}>{p.sub}</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: p.color }}>{formatUGX(p.amount)}</div>
            <div style={{ fontSize: 11, color: C.textLight }}>{formatPct(pct(p.amount))}</div>
          </div>
        </div>
      ))}

      {/* Sum reconciliation hint */}
      <div style={{ fontSize: 11, color: C.textLight, marginTop: 4, display: "flex", alignItems: "center", gap: 6 }}>
        <FiInfo size={11} />
        Shares add up to {formatUGX(total)}
        {fee != null && Math.abs(total - Number(fee)) > 1
          ? ` — note: split sum differs from the fee (${formatUGX(fee)}).`
          : " — the full delivery fee."}
      </div>

      {/* ── Flags as friendly badges ── */}
      {flagList.length > 0 ? (
        <div style={{ marginTop: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 8 }}>
            <FiFlag size={12} color={C.textMid} />
            <span style={{ fontSize: 10, fontWeight: 800, color: C.textLight, textTransform: "uppercase", letterSpacing: "0.6px" }}>
              Pricing flags
            </span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {flagList.map((flag) => {
              const meta = flagMeta(flag);
              const tc = toneColors(meta.tone);
              return (
                <div key={flag} style={{
                  display: "flex", gap: 10, alignItems: "flex-start",
                  background: tc.soft, border: `1px solid ${tc.color}33`,
                  borderRadius: C.radiusSm, padding: "8px 11px",
                }}>
                  <span style={{ ...badgeStyle(tc.color, "#fff"), flexShrink: 0, marginTop: 1 }}>
                    {meta.label}
                  </span>
                  {meta.hint ? (
                    <span style={{ fontSize: 11, color: C.textMid, lineHeight: 1.4 }}>{meta.hint}</span>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
